// The brand-family tokens (theme/tokens.css) as values for sx and inline styles. Each one is a CSS variable, so it
// follows the active theme (Light, Natural, Dark) without a re-render. Use these instead of hex literals for any
// surface, ink or line; Hauser's status colours stay in the MUI palette.

export const THEME_NAMES = ['light', 'natural', 'dark'] as const;
export type ThemeName = (typeof THEME_NAMES)[number];

// Hauser ships two modes, both on slate (Dark) so the radar, HomeDNA and the urgency palette are tuned once: Night is
// the room with the lights off (the dark ground), Day the same slate cards on the lit chalk ground (theme/hauser.css,
// keyed off <html data-mode>). Paper (Natural) stays in the token set but no longer backs a mode.
export const MODES = ['day', 'night'] as const;
export type Mode = (typeof MODES)[number];
export const MODE_LABELS: Record<Mode, string> = { day: 'Day', night: 'Night' };
export const MODE_THEME: Record<Mode, ThemeName> = { day: 'dark', night: 'dark' };
export const TOOLS_THEME: ThemeName = 'dark';

export const THEME_LABELS: Record<ThemeName, string> = {
  light: 'Light',
  natural: 'Natural',
  dark: 'Dark',
};

export const tx = {
  // ink
  text: 'var(--tx-text)',
  onText: 'var(--tx-on-text)',
  muted: 'var(--tx-muted)',
  faint: 'var(--tx-faint)',
  line: 'var(--tx-line)',
  ink: (alpha: number) => `rgba(var(--tx-ink-rgb), ${alpha})`,

  // tonal fills: a step up for hover, another for pressed
  fill: 'var(--tx-fill)',
  fill2: 'var(--tx-fill-2)',
  fill3: 'var(--tx-fill-3)',

  // surfaces
  ground: 'var(--tx-ground)',
  panel: 'var(--tx-glass-panel)',
  card: 'var(--tx-glass-card)',
  cardFill: 'var(--tx-card-fill)',
  dialog: 'var(--tx-dialog)',
  solid: 'var(--tx-solid)',
  backdrop: 'var(--tx-backdrop)',
  tooltip: 'var(--tx-tooltip-bg)',

  // light and depth
  deboss: 'var(--tx-deboss)',
  shadow: 'var(--tx-shadow)',
  shadowSm: 'var(--tx-shadow-sm)',
  blur: 'var(--tx-blur)',
  blurPanel: 'var(--tx-blur-panel)',
  surfaceLight: 'var(--tx-surface-light)',

  // the selected tab / segment / chip (a pressed well on Light and Natural, a lifted tile on Dark)
  selBg: 'var(--tx-sel-bg)',
  selShadow: 'var(--tx-sel-shadow)',

  // brand family
  lime: 'var(--tx-lime)',
  limeHover: 'var(--tx-lime-hover)',
  onLime: 'var(--tx-on-lime)',
  chalk: 'var(--tx-chalk)',
  todayInk: 'var(--tx-today-ink)',
  danger: 'var(--tx-danger)',
  switchOff: 'var(--tx-switch-off)',

  // Hauser's chalk ground and the ink for anything placed straight on it
  groundInk: 'var(--hx-ground-ink)',
  groundMuted: 'var(--hx-ground-muted)',
  drop: 'var(--hx-drop)',

  // shape
  rSheet: 24,
  rTile: 16,
  rCtl: 12,
  rThumb: 9,
  h: 40,

  // motion
  ease: 'cubic-bezier(.2, .9, .3, 1)',
} as const;

// A raised surface: the panel recipe (glass on Light and Natural, matte on Dark) with its top-left light
export const sheetSx = {
  backgroundColor: tx.panel,
  backgroundImage: tx.surfaceLight,
  backdropFilter: tx.blurPanel,
  WebkitBackdropFilter: tx.blurPanel,
  boxShadow: tx.shadow,
  borderRadius: `${tx.rSheet}px`,
} as const;

// A tile inside a sheet (Tradehaus .tx-vz-card): tonal, flat, no shadow or border
export const tileSx = {
  backgroundColor: tx.ink(0.05),
  borderRadius: `${tx.rTile}px`,
} as const;

// A chart tooltip (Tradehaus .tx-tip): the theme's glass, a small shadow, no sheen
export const tipSx = {
  background: 'var(--tx-glass)',
  backdropFilter: 'var(--tx-blur)',
  WebkitBackdropFilter: 'var(--tx-blur)',
  boxShadow: 'var(--tx-shadow-sm)',
  borderRadius: '12px',
} as const;
